import React, { useCallback, useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { useResizeObserver } from '../../hooks';
import { getPdfFileContent } from "../../common/services";
import { PDF_TYPE } from "../../constants";
import PageLoader from "../page-loader";
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const PDFViewer = ({ 
    file, 
    docId,
    pageNumber,
    searchText,
    onDocumentLoadSuccess: onDocumentLoadSuccessProp,
    onDocumentLoadError 
}) => {
  const [numPages, setNumPages] = useState(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [isFetching, setIsFetching] = useState(false);
  const [pdfData, setPdfData] = useState(null);
  const [hasError, setHasError] = useState(false);
  const containerRef = useRef(null);
  const pageRefs = useRef({});

  useResizeObserver(containerRef.current, (ref) => {
    setContainerWidth(ref.width);
  });

  const fetchPdfContent = useCallback(async () => {
    if (typeof file === "string") {
      setPdfData(file);
      return;
    }

    if (file && file.Key && !hasError && !isFetching && !pdfData) {
      try {
        setIsFetching(true);
        const pdfFileContent = await getPdfFileContent(file, {
          type: PDF_TYPE.DOC,
          id: docId
        });
        
        const base64String = btoa(
          new Uint8Array(pdfFileContent).reduce((data, byte) => data + String.fromCharCode(byte), '')
        );
    
        setPdfData(base64String);
        setHasError(false);
      } catch (error) {
        console.error("Error fetching PDF content in PDFViewer", error);
        setHasError(true);
        onDocumentLoadError && onDocumentLoadError(error);
      } finally {
        setIsFetching(false);
      }
    }
  }, [file, docId, onDocumentLoadError, hasError, isFetching, pdfData]);

  useEffect(() => {
    setHasError(false);
    setPdfData(null);
    setNumPages(null);
  }, [file]);

  useEffect(() => {
    fetchPdfContent();
  }, [fetchPdfContent]);

  useEffect(() => {
    if (numPages && pageNumber) {
        const pageNumStr = pageNumber.toString();
        let targetPage = null;

        if (!isNaN(pageNumber)) {
            targetPage = Number(pageNumber);
        } else if (pageNumStr.includes('-')) {
            const rangeArr = pageNumStr.split('-');
            const start = Number(rangeArr[0]);
            if (!isNaN(start)) {
                targetPage = start;
            }
        }

        if (targetPage && pageRefs.current[targetPage]) {
            setTimeout(() => {
                pageRefs.current[targetPage].scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 500); // Small delay to ensure rendering is stable
        }
    }
  }, [numPages, pageNumber]);

  const onDocumentLoadSuccess = useCallback((e) => {
    const { numPages } = e;
    setNumPages(numPages);
    onDocumentLoadSuccessProp && onDocumentLoadSuccessProp(e);
  }, [onDocumentLoadSuccessProp]);

  const handleCustomTextRenderer = useCallback((e) => {
      const { str: text } = e;
      if (!searchText || !text) {
          return text;
      }

      const searchTerms = (Array.isArray(searchText) ? searchText : searchText.split('\n')).map(term => term.trim()).filter(Boolean);

      if (searchTerms.length === 0) {
          return text;
      }

      const escapedSearchTerms = searchTerms.map(term => term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
      const regex = new RegExp(`(${escapedSearchTerms.join('|')})`, 'gi');
      const parts = text.split(regex);
      const lowerCaseSearchTerms = searchTerms.map(term => term.toLowerCase());

      return parts.map((part, i) =>
          part && lowerCaseSearchTerms.includes(part.toLowerCase()) ? <mark key={i}>{part}</mark> : part
      );
  }, [searchText]);

  if (isFetching || (!pdfData && !hasError)) return <PageLoader />;

  return (
    <div className="bimmatch-pdf-viewer-container" ref={containerRef}>
      <Document
        file={`data:application/pdf;base64,${pdfData}`}
        onLoadSuccess={onDocumentLoadSuccess}
        onLoadError={onDocumentLoadError}
        loading={<PageLoader />}
        error={<div>Failed to load PDF.</div>}
      >
        {Array.from(new Array(numPages || 0), (_, i) => i + 1).map((pageNum) => (
          <div 
            className="pdf-page-wrapper" 
            key={`page_${pageNum}`}
            ref={(el) => (pageRefs.current[pageNum] = el)}
          >
            <Page 
              pageNumber={pageNum} 
              width={containerWidth}
              customTextRenderer={handleCustomTextRenderer}
            />
          </div>
        ))}
      </Document>
    </div>
  );
};

export default PDFViewer;
